import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { trpcClient, useTRPC } from '@/lib/trpc'
import { LANGUAGE_ENUM_VALUES } from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { Bell, Loader2, RefreshCw, Send } from 'lucide-react'
import { useState } from 'react'

type Language = (typeof LANGUAGE_ENUM_VALUES)[number]
type Copy = { title: string, body: string }
type Audience = 'trial' | 'all'
type LanguageSync = {
  at: Date
  totalUsers: number
  breakdown: Record<string, number>
}
type PublishResult = {
  audience: Audience
  verified: boolean
  threadId: string
  recipientCount: number
  languageBreakdown: Partial<Record<Language, number>>
  isUnlisted: boolean
  alreadyPublished: boolean
}

const LANGUAGE_LABELS: Record<Language, string> = {
  zh: '🇨🇳 简体中文',
  tw: '🇹🇼 繁體中文',
  en: '🇬🇧 English',
  fr: '🇫🇷 Français',
  es: '🇪🇸 Español',
  ja: '🇯🇵 日本語',
  ko: '🇰🇷 한국어',
  ar: '🇸🇦 العربية',
}

const SYNC_MAX_AGE_MS = 5 * 60 * 1000

function isPlausibleEmail(value: string): boolean {
  const parts = value.trim().split('@')
  return parts.length === 2
    && parts[0]!.length > 0
    && parts[1]!.includes('.')
    && !/\s/.test(value)
}

function threadPreviewLanguage(result: PublishResult): Language {
  const languages = LANGUAGE_ENUM_VALUES.filter(
    language => (result.languageBreakdown[language] ?? 0) > 0,
  )
  return result.audience === 'trial' && languages.length === 1
    ? languages[0]!
    : 'en'
}

export function LocalizedAnnouncementSection() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const audiencePreview = useQuery({
    ...trpc.batchRouter.previewNewsCampaignAudience.queryOptions(),
    enabled: false,
    retry: false,
  })
  const [campaignKey, setCampaignKey] = useState('')
  const [audience, setAudience] = useState<Audience>('trial')
  const [trialEmail, setTrialEmail] = useState('')
  const [isPinned, setIsPinned] = useState(false)
  const [editingLanguage, setEditingLanguage] = useState<Language>('en')
  const [translations, setTranslations] = useState<
    Partial<Record<Language, Copy>>
  >({})
  const [languageSync, setLanguageSync] = useState<LanguageSync | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [showAllConfirm, setShowAllConfirm] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState('')
  const [result, setResult] = useState<PublishResult | null>(null)
  const [previewRequested, setPreviewRequested] = useState(false)

  const copy = translations[editingLanguage] ?? { title: '', body: '' }
  const completeLanguages = LANGUAGE_ENUM_VALUES.filter((language) => {
    const candidate = translations[language]
    return Boolean(candidate?.title.trim() && candidate.body.trim())
  })

  const updateCopy = (field: keyof Copy, value: string) => {
    setTranslations(previous => ({
      ...previous,
      [editingLanguage]: {
        title: previous[editingLanguage]?.title ?? '',
        body: previous[editingLanguage]?.body ?? '',
        [field]: value,
      },
    }))
    setError('')
    setResult(null)
  }

  const refreshLanguages = async () => {
    setSyncing(true)
    setLanguageSync(null)
    setPreviewRequested(false)
    setError('')
    setResult(null)
    try {
      const response = await trpcClient.batchRouter.updateUserLanguages.mutate()
      if (!response.success || !Number.isInteger(response.details.totalUsers)) {
        throw new Error(response.message || t('admin.announcement.syncFailed'))
      }
      setLanguageSync({
        at: new Date(),
        totalUsers: response.details.totalUsers,
        breakdown: response.details.languageBreakdown,
      })
    }
    catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t('admin.announcement.syncFailed'),
      )
    }
    finally {
      setSyncing(false)
    }
  }

  const previewAudience = () => {
    if (
      !languageSync
      || Date.now() - languageSync.at.getTime() > SYNC_MAX_AGE_MS
    ) {
      setError(t('admin.announcement.syncRequired'))
      return
    }
    setPreviewRequested(true)
    void audiencePreview.refetch()
  }

  const validate = (): string | null => {
    if (!/^[a-z0-9][a-z0-9._-]{0,159}$/.test(campaignKey.trim()))
      return t('admin.announcement.keyRequired')
    if (audience === 'trial' && !isPlausibleEmail(trialEmail))
      return t('admin.announcement.emailRequired')
    if (
      !languageSync
      || Date.now() - languageSync.at.getTime() > SYNC_MAX_AGE_MS
    ) {
      return t('admin.announcement.syncRequired')
    }
    if (completeLanguages.length === 0)
      return t('admin.announcement.copyRequired')
    if (
      LANGUAGE_ENUM_VALUES.some((language) => {
        const candidate = translations[language]
        return (
          candidate
          && Boolean(candidate.title.trim()) !== Boolean(candidate.body.trim())
        )
      })
    ) {
      return t('admin.announcement.partialCopy')
    }
    if (
      audience === 'all'
      && completeLanguages.length !== LANGUAGE_ENUM_VALUES.length
    ) {
      return t('admin.announcement.allLanguagesRequired')
    }
    return null
  }

  const publish = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      setShowAllConfirm(false)
      return
    }

    const completeTranslations = Object.fromEntries(
      completeLanguages.map(language => [
        language,
        {
          title: translations[language]!.title.trim(),
          body: translations[language]!.body.trim(),
        },
      ]),
    ) as Partial<Record<Language, Copy>>

    setPublishing(true)
    setShowAllConfirm(false)
    setError('')
    setResult(null)
    try {
      const common = {
        campaignKey: campaignKey.trim(),
        translations: completeTranslations,
      }
      const response
        = audience === 'trial'
          ? await trpcClient.forumRouter.createLocalizedAnnouncement.mutate({
              ...common,
              specificEmails: [trialEmail.trim()],
            })
          : await trpcClient.forumRouter.createLocalizedAnnouncement.mutate({
              ...common,
              broadcastAll: true,
              isPinned,
            })
      const breakdownCount = Object.values(response.languageBreakdown).reduce(
        (sum, count) => sum + (count ?? 0),
        0,
      )
      const verified
        = audience === 'trial'
          ? response.recipientCount === 1
          && response.isUnlisted
          && breakdownCount === 1
          : !response.isUnlisted && breakdownCount === response.recipientCount
      setResult({ ...response, audience, verified })
    }
    catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t('admin.announcement.publishFailed'),
      )
    }
    finally {
      setPublishing(false)
    }
  }

  const startPublish = () => {
    setError('')
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    if (audience === 'all') {
      setConfirmText('')
      setShowAllConfirm(true)
      return
    }
    void publish()
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-6 py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="size-5" />
            {t('admin.announcement.title')}
          </CardTitle>
          <CardDescription>
            {t('admin.announcement.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3 rounded-lg border p-4">
            <h3 className="font-semibold">{t('admin.announcement.step0')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('admin.announcement.step0Description')}
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={refreshLanguages}
              disabled={syncing || publishing || audiencePreview.isFetching}
            >
              {syncing ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 size-4" />
              )}
              {t('admin.announcement.refreshLanguages')}
            </Button>
            {languageSync && (
              <div
                className="rounded-md bg-emerald-500/10 p-3 text-sm"
                role="status"
              >
                <p>
                  {t('admin.announcement.synced')} {languageSync.totalUsers} ·{' '}
                  {languageSync.at.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {Object.entries(languageSync.breakdown)
                    .map(([language, count]) => `${language}: ${count}`)
                    .join(' · ')}
                </p>
              </div>
            )}
          </div>

          <section
            aria-labelledby="announcement-audience-preview-heading"
            className="space-y-3 rounded-lg border p-4"
          >
            <h3 id="announcement-audience-preview-heading" className="font-semibold">
              {t('admin.announcement.previewTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('admin.announcement.previewDescription')}
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={previewAudience}
              disabled={!languageSync || syncing || publishing || audiencePreview.isFetching}
            >
              {audiencePreview.isFetching ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 size-4" />
              )}
              {t('admin.announcement.previewAudience')}
            </Button>
            {previewRequested && audiencePreview.isFetching && (
              <p role="status" className="text-sm text-muted-foreground">
                {t('admin.announcement.previewLoading')}
              </p>
            )}
            {previewRequested && !audiencePreview.isFetching && audiencePreview.isError && (
              <p role="alert" className="text-sm text-destructive">
                {t('admin.announcement.previewFailed')}
              </p>
            )}
            {previewRequested && !audiencePreview.isFetching && audiencePreview.isSuccess && (
              <div role="status" className="space-y-3">
                <dl className="space-y-3">
                  {(['announcement', 'email'] as const).map(channel => (
                    <div key={channel} className="rounded-md bg-muted/40 p-3 text-sm">
                      <dt className="font-medium">
                        {t(`admin.announcement.previewChannel.${channel}`)}
                      </dt>
                      <dd className="mt-1">
                        <strong className="text-lg">
                          {audiencePreview.data[channel].recipientCount}
                        </strong>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {LANGUAGE_ENUM_VALUES.map(language =>
                            `${language}: ${audiencePreview.data[channel].languageBreakdown[language] ?? 0}`,
                          ).join(' · ')}
                        </p>
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.announcement.previewSnapshot')}
                </p>
              </div>
            )}
          </section>

          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="font-semibold">{t('admin.announcement.step1')}</h3>
            <div className="space-y-2">
              <Label htmlFor="announcement-campaign-key">
                {t('admin.announcement.campaignKey')}
              </Label>
              <Input
                id="announcement-campaign-key"
                value={campaignKey}
                onChange={(event) => {
                  setCampaignKey(event.target.value)
                  setResult(null)
                }}
                placeholder="wolf-brothers-launch-2026-09-23-trial"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t('admin.announcement.keyDescription')}
              </p>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                {t('admin.announcement.audience')}
              </legend>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="announcement-audience"
                  checked={audience === 'trial'}
                  onChange={() => {
                    setAudience('trial')
                    setResult(null)
                  }}
                />
                {t('admin.announcement.trial')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="announcement-audience"
                  checked={audience === 'all'}
                  onChange={() => {
                    setAudience('all')
                    setResult(null)
                  }}
                />
                {t('admin.announcement.allUsers')}
              </label>
            </fieldset>

            {audience === 'trial' ? (
              <div className="space-y-2">
                <Label htmlFor="announcement-trial-email">
                  {t('admin.announcement.trialEmail')}
                </Label>
                <Input
                  id="announcement-trial-email"
                  type="email"
                  value={trialEmail}
                  onChange={(event) => {
                    setTrialEmail(event.target.value)
                    setResult(null)
                  }}
                  placeholder="name@example.com"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  {t('admin.announcement.trialDescription')}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="rounded-md bg-amber-500/10 p-3 text-sm">
                  {t('admin.announcement.allUsersDescription')}
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isPinned}
                    onChange={(event) => {
                      setIsPinned(event.target.checked)
                      setResult(null)
                    }}
                  />
                  {t('admin.announcement.pinned')}
                </label>
              </div>
            )}

            <div className="space-y-3 border-t pt-4">
              <Label htmlFor="announcement-language">
                {t('admin.announcement.copyLanguage')}
              </Label>
              <select
                id="announcement-language"
                value={editingLanguage}
                onChange={event =>
                  setEditingLanguage(event.target.value as Language)}
                className="block rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {LANGUAGE_ENUM_VALUES.map(language => (
                  <option key={language} value={language}>
                    {LANGUAGE_LABELS[language]}{' '}
                    {completeLanguages.includes(language) ? '✓' : ''}
                  </option>
                ))}
              </select>
              <div className="space-y-2">
                <Label htmlFor="announcement-copy-title">
                  {t('admin.announcement.copyTitle')}
                </Label>
                <Input
                  id="announcement-copy-title"
                  value={copy.title}
                  onChange={event => updateCopy('title', event.target.value)}
                  maxLength={150}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="announcement-copy-body">
                  {t('admin.announcement.copyBody')}
                </Label>
                <Textarea
                  id="announcement-copy-body"
                  value={copy.body}
                  onChange={event => updateCopy('body', event.target.value)}
                  className="min-h-[160px]"
                  maxLength={10000}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.announcement.completeLanguages')}{' '}
                {completeLanguages
                  .map(language => LANGUAGE_LABELS[language])
                  .join(', ') || '—'}
              </p>
            </div>
          </div>

          <Button
            type="button"
            onClick={startPublish}
            disabled={syncing || publishing}
            variant={audience === 'all' ? 'destructive' : 'default'}
          >
            {publishing ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Send className="mr-2 size-4" />
            )}
            {audience === 'trial'
              ? t('admin.announcement.publishTrial')
              : t('admin.announcement.publishAll')}
          </Button>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          {result && (
            <div
              className={`space-y-2 rounded-md p-4 text-sm ${result.verified ? 'bg-emerald-500/10' : 'bg-destructive/10 text-destructive'}`}
              role={result.verified ? 'status' : 'alert'}
            >
              <p className="font-semibold">
                {!result.verified
                  ? t('admin.announcement.unexpectedResult')
                  : result.alreadyPublished
                    ? t('admin.announcement.alreadyPublished')
                    : t('admin.announcement.published')}
              </p>
              {!result.verified && (
                <p>
                  {result.audience === 'trial'
                    ? t('admin.announcement.trialExpected')
                    : t('admin.announcement.allExpected')}
                </p>
              )}
              <p>
                {t('admin.announcement.threadId')} {result.threadId}
              </p>
              <p>
                {t('admin.announcement.recipients')} {result.recipientCount}
              </p>
              <p>
                {t('admin.announcement.scope')}{' '}
                {result.isUnlisted
                  ? t('admin.announcement.unlisted')
                  : t('admin.announcement.public')}
              </p>
              <p>
                {Object.entries(result.languageBreakdown)
                  .map(([language, count]) => `${language}: ${count}`)
                  .join(' · ')}
              </p>
              <a
                className="underline"
                href={`https://werewolf.mentiss.ai/${threadPreviewLanguage(result)}/forum/thread/${encodeURIComponent(result.threadId)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('admin.announcement.openThread')}
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {showAllConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="announcement-confirm-title"
        >
          <div className="mx-4 w-full max-w-md space-y-4 rounded-lg border bg-background p-6">
            <h3
              id="announcement-confirm-title"
              className="text-lg font-semibold"
            >
              {t('admin.announcement.confirmTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('admin.announcement.confirmDescription')}
            </p>
            <p className="text-sm">
              {t('admin.announcement.synced')} {languageSync?.totalUsers ?? '—'}{' '}
              · {t('admin.announcement.completeLanguages')}{' '}
              {completeLanguages.join(', ')}
            </p>
            <div className="space-y-2">
              <Label htmlFor="announcement-confirm-text">
                {t('admin.announcement.confirmPrompt')}
              </Label>
              <Input
                id="announcement-confirm-text"
                value={confirmText}
                onChange={event => setConfirmText(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAllConfirm(false)}
              >
                {t('admin.announcement.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={publish}
                disabled={confirmText !== 'PUBLISH' || publishing}
              >
                {t('admin.announcement.confirmPublish')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
